import React, { useState, useEffect, useRef, useMemo } from 'react';
import teacherService from '../../services/api/teacher';
import { useQuery } from 'react-query';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { 
  DollarSign, 
  TrendingUp, 
  Download, 
  CreditCard, 
  ShieldCheck, 
  ChevronLeft, 
  Sparkles, 
  BarChart3, 
  Users, 
  PieChart,
  Info,
  Briefcase
} from 'lucide-react';

const TeacherEarnings = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  // جلب بيانات الإيرادات بناءً على الفلاتر (إعدادات محسّنة)
  const { data: earnings, isLoading } = useQuery(
    ['teacher-earnings', selectedPeriod, selectedYear],
    () => teacherService.getTeacherEarnings({ period: selectedPeriod, year: selectedYear }),
    {
      keepPreviousData: true,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  // حساب إيرادات الفترة الحالية بشكل صحيح
  const periodRevenue = useMemo(() => {
    const data = earnings?.earnings?.data;
    if (!data) return 0;

    switch (selectedPeriod) {
      case 'week':
        return data.weeklyBreakdown?.at(-1)?.revenue || 0;
      case 'month':
        return data.monthlyBreakdown?.at(-1)?.revenue || 0;
      case 'quarter':
        return data.quarterlyBreakdown?.at(-1)?.revenue || 0;
      case 'year':
        return data.yearlyBreakdown?.at(-1)?.revenue || 0;
      default:
        return 0;
    }
  }, [earnings, selectedPeriod]);

  // خيارات السنة المُحسّنة
  const yearOptions = useMemo(() => (
    [0, 1, 2].map(i => selectedYear - i)
  ), [selectedYear]);

  // إحصائيات مُحسّنة
  const stats = useMemo(() => [
    { label: 'إجمالي الإيرادات', value: `${earnings?.earnings?.data?.totalEarnings || 0} ج.م`, icon: DollarSign, color: 'text-emerald-600', bg: 'bg-emerald-50', growth: '+١٢%', positive: true },
    { label: 'إيرادات الشهر', value: `${periodRevenue} ج.م`, icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-50', growth: '+8%', positive: true },
    { label: 'مدفوعات معلقة', value: `${earnings?.earnings?.data?.pendingPayments || 0} ج.م`, icon: CreditCard, color: 'text-purple-600', bg: 'bg-purple-50', growth: '-3%', positive: false },
    { label: 'الاشتراكات النشطة', value: earnings?.earnings?.data?.totalEnrollments || 0, icon: Users, color: 'text-orange-600', bg: 'bg-orange-50', growth: '+١٥%', positive: true }
  ], [earnings, periodRevenue]);

  useEffect(() => {
    if (!chartRef.current) return;
    if (!earnings?.earnings?.data) return;

    const hasData =
      earnings.earnings.data.monthlyBreakdown?.length ||
      earnings.earnings.data.weeklyBreakdown?.length ||
      earnings.earnings.data.yearlyBreakdown?.length ||
      earnings.earnings.data.quarterlyBreakdown?.length;

    if (!hasData) return;

    // Clear existing chart if it exists
    if (chartInstance.current) {
      const canvas = chartInstance.current.ctx?.canvas;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      chartInstance.current = null;
    }

    const ctx = chartRef.current.getContext('2d');
    
    // Prepare chart data based on period
    let chartData = [];
    let labels = [];
    
    if (selectedPeriod === 'month') {
      const monthlyData = earnings.earnings.data.monthlyBreakdown || [];
      const monthNames = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
      
      // Sort by month
      monthlyData.sort((a, b) => a.month - b.month);
      
      chartData = monthlyData.map(item => item.revenue);
      labels = monthlyData.map(item => monthNames[item.month - 1]);
    } else if (selectedPeriod === 'year') {
      const yearlyData = earnings.earnings.data.yearlyBreakdown || [];
      chartData = yearlyData.map(item => item.revenue);
      labels = yearlyData.map(item => item.year.toString());
    } else if (selectedPeriod === 'week') {
      const weeklyData = earnings.earnings.data.weeklyBreakdown || [];
      chartData = weeklyData.map(item => item.revenue);
      labels = weeklyData.map(item => `أسبوع ${item.week}`);
    } else if (selectedPeriod === 'quarter') {
      const quarterlyData = earnings.earnings.data.quarterlyBreakdown || [];
      const quarterNames = ['الربع الأول', 'الربع الثاني', 'الربع الثالث', 'الربع الرابع'];
      
      // Sort by quarter
      quarterlyData.sort((a, b) => a.quarter - b.quarter);
      
      chartData = quarterlyData.map(item => item.revenue);
      labels = quarterlyData.map(item => quarterNames[item.quarter - 1]);
    }

    // Create gradient
    const gradient = ctx.createLinearGradient(0, 0, 0, 300);
    gradient.addColorStop(0, 'rgba(99, 102, 241, 0.3)');
    gradient.addColorStop(1, 'rgba(99, 102, 241, 0.01)');

    // Simple chart implementation using canvas
    chartInstance.current = {
      ctx,
      data: {
        labels,
        datasets: [{
          label: 'الإيرادات (ج.م)',
          data: chartData,
          borderColor: '#6366f1',
          backgroundColor: gradient,
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#6366f1',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 6,
          pointHoverRadius: 8
        }]
      },
      render: function() {
        const canvas = this.ctx.canvas;
        const width = canvas.width = canvas.offsetWidth;
        const height = canvas.height = canvas.offsetHeight;
        
        this.ctx.clearRect(0, 0, width, height);
        
        if (this.data.datasets[0].data.length === 0) {
          this.ctx.fillStyle = '#9ca3af';
          this.ctx.font = '14px sans-serif';
          this.ctx.textAlign = 'center';
          this.ctx.fillText('لا توجد بيانات متاحة', width / 2, height / 2);
          return;
        }

        // Simple line chart drawing
        const data = this.data.datasets[0].data;
        const maxValue = Math.max(...data, 1);
        const padding = 40;
        const chartWidth = width - padding * 2;
        const chartHeight = height - padding * 2;
        const stepX = chartWidth / (data.length - 1 || 1);

        // Draw grid lines
        this.ctx.strokeStyle = '#e5e7eb';
        this.ctx.lineWidth = 1;
        for (let i = 0; i <= 5; i++) {
          const y = padding + (chartHeight / 5) * i;
          this.ctx.beginPath();
          this.ctx.moveTo(padding, y);
          this.ctx.lineTo(width - padding, y);
          this.ctx.stroke();
        }

        // Draw the line
        this.ctx.strokeStyle = this.data.datasets[0].borderColor;
        this.ctx.lineWidth = this.data.datasets[0].borderWidth;
        this.ctx.beginPath();
        
        data.forEach((value, index) => {
          const x = padding + stepX * index;
          const y = padding + chartHeight - (value / maxValue) * chartHeight;
          
          if (index === 0) {
            this.ctx.moveTo(x, y);
          } else {
            this.ctx.lineTo(x, y);
          }
        });
        
        this.ctx.stroke();

        // Draw area under line
        this.ctx.fillStyle = gradient;
        this.ctx.beginPath();
        data.forEach((value, index) => {
          const x = padding + stepX * index;
          const y = padding + chartHeight - (value / maxValue) * chartHeight;
          
          if (index === 0) {
            this.ctx.moveTo(x, height - padding);
            this.ctx.lineTo(x, y);
          } else {
            this.ctx.lineTo(x, y);
          }
        });
        this.ctx.lineTo(padding + stepX * (data.length - 1), height - padding);
        this.ctx.closePath();
        this.ctx.fill();

        // Draw points
        this.ctx.fillStyle = this.data.datasets[0].pointBackgroundColor;
        data.forEach((value, index) => {
          const x = padding + stepX * index;
          const y = padding + chartHeight - (value / maxValue) * chartHeight;
          
          this.ctx.beginPath();
          this.ctx.arc(x, y, this.data.datasets[0].pointRadius, 0, Math.PI * 2);
          this.ctx.fill();
          
          this.ctx.strokeStyle = this.data.datasets[0].pointBorderColor;
          this.ctx.lineWidth = this.data.datasets[0].pointBorderWidth;
          this.ctx.stroke();
        });

        // Draw labels
        this.ctx.fillStyle = '#6b7280';
        this.ctx.font = '12px sans-serif';
        this.ctx.textAlign = 'center';
        
        this.data.labels.forEach((label, index) => {
          const x = padding + stepX * index;
          this.ctx.fillText(label, x, height - 10);
        });

        // Draw values
        data.forEach((value, index) => {
          const x = padding + stepX * index;
          const y = padding + chartHeight - (value / maxValue) * chartHeight;
          
          this.ctx.fillStyle = '#374151';
          this.ctx.font = 'bold 11px sans-serif';
          this.ctx.fillText(value.toString(), x, y - 10);
        });
      }
    };

    // Initial render
    chartInstance.current.render();

    // Cleanup
    return () => {
      if (chartInstance.current) {
        const canvas = chartInstance.current.ctx?.canvas;
        if (canvas) {
          const ctx = canvas.getContext('2d');
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
        chartInstance.current = null;
      }
    };
  }, [earnings, selectedPeriod, selectedYear]);

  if (isLoading && !earnings) {
    return (
      <div className="flex flex-col items-center justify-center py-24 min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse uppercase tracking-widest text-xs">جاري مراجعة السجلات المالية...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-6 min-h-screen bg-gray-50/30">
      
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 animate-in fade-in duration-500">
        <div className="space-y-1">
          <h1 className="text-3xl lg:text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
            <DollarSign className="text-primary-600 bg-primary-50 p-1.5 rounded-xl" size={36} />
            إدارة الإيرادات المالية
          </h1>
          <p className="text-gray-500 font-medium flex items-center gap-2 mr-12">
            <ShieldCheck className="text-emerald-500" size={18} />
            تتبع أرباح الكورسات والاشتراكات الشهرية بكل شفافية وأمان
          </p>
        </div>

        <button className="bg-white border border-gray-100 text-gray-700 px-8 py-3.5 rounded-2xl font-black shadow-sm hover:bg-primary-600 hover:text-white hover:border-primary-600 transition-all flex items-center gap-2 transform active:scale-95">
            <Download size={20} /> تحميل تقرير PDF
        </button>
      </div>

      {/* Modern Analytics Pills Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, i) => (
          <div key={i} className="bg-white p-6 rounded-[2.2rem] border border-gray-100 shadow-sm hover:shadow-md transition-all group relative overflow-hidden">
            <div className="flex items-center justify-between mb-6">
                <div className={`p-4 ${stat.bg} ${stat.color} rounded-2xl group-hover:scale-110 transition-transform`}>
                    <stat.icon size={24} />
                </div>
                <div className={`flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-full border ${stat.positive ? 'bg-green-50 text-green-600 border-green-100' : 'bg-rose-50 text-rose-600 border-rose-100'}`}>
                    {stat.positive ? <TrendingUp size={10} /> : <TrendingUp size={10} className="rotate-180" />}
                    {stat.growth}
                </div>
            </div>
            <div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] leading-none mb-2">{stat.label}</p>
                <p className="text-2xl font-black text-gray-900 leading-none tracking-tight">{stat.value}</p>
            </div>
            {/* Decorative background circle */}
            <div className={`absolute -bottom-4 -left-4 w-12 h-12 ${stat.bg} opacity-20 rounded-full blur-xl`}></div>
          </div>
        ))}
      </div>

      {/* Advanced Filter Toolkit */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-6 flex flex-col md:flex-row items-center gap-6">
        <div className="flex items-center gap-3 text-gray-400 font-bold text-xs uppercase tracking-widest ml-4">
            <BarChart3 size={18} /> تصفية البيانات:
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 flex-1">
            <div className="relative">
                <select
                    value={selectedPeriod}
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                >
                    <option value="week">التقرير الأسبوعي</option>
                    <option value="month">التقرير الشهري</option>
                    <option value="quarter">تقرير الربع سنوي</option>
                    <option value="year">التقرير السنوي</option>
                </select>
                <ChevronLeft className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 -rotate-90 pointer-events-none" size={16} />
            </div>

            <div className="relative">
                <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                >
                    {yearOptions.map(year => (
                        <option key={year} value={year}>{year}</option>
                    ))}
                </select>
                <ChevronLeft className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 -rotate-90 pointer-events-none" size={16} />
            </div>

            <div className="lg:col-span-2 flex items-center justify-end">
                <p className="text-xs font-bold text-gray-400 ml-4 hidden lg:block uppercase tracking-tighter">✨ يتم تحديث الرسوم البيانية والجداول تلقائياً</p>
            </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Earnings Chart Section (2/3 Width) */}
        <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 group hover:shadow-xl transition-all duration-500">
                <div className="flex items-center justify-between mb-10">
                    <h2 className="text-xl font-black text-gray-900 flex items-center gap-3 tracking-tighter uppercase">
                        <PieChart size={22} className="text-primary-600" /> الرسم البياني للنمو المالي
                    </h2>
                    <span className="text-[10px] font-black text-gray-400 border border-gray-100 px-3 py-1 rounded-full bg-gray-50 uppercase tracking-widest">تحديث مباشر</span>
                </div>
                
                <div className="h-72 bg-gray-50/50 rounded-[2rem] border border-gray-200 relative overflow-hidden group/chart">
                    <canvas 
                        ref={chartRef}
                        className="w-full h-full"
                        style={{ width: '100%', height: '100%' }}
                    />
                </div>
            </div>

            {/* Top Performing Courses Section */}
            <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden group hover:shadow-xl transition-all duration-500">
                <div className="p-8 border-b border-gray-50 flex items-center justify-between bg-gray-50/30">
                    <h2 className="text-xl font-black text-gray-900 tracking-tighter uppercase">أفضل الكورسات أداءً</h2>
                    <span className="text-[10px] font-bold text-primary-600 bg-primary-50 px-3 py-1 rounded-full border border-primary-100 uppercase tracking-widest">TOP 5</span>
                </div>
                
                <div className="overflow-x-auto">
                    {earnings?.earnings?.data?.topPerformingCourses?.length === 0 ? (
                        <div className="py-20 text-center text-gray-400">
                            <Info size={40} className="mx-auto mb-4 opacity-20" />
                            <p className="font-black uppercase tracking-widest text-xs">لا توجد كورسات مسجلة في هذه الفترة</p>
                        </div>
                    ) : (
                        <table className="w-full">
                            <thead>
                                <tr className="bg-gray-50">
                                    <th className="px-8 py-4 text-right text-xs font-black text-gray-500 uppercase tracking-widest">الترتيب</th>
                                    <th className="px-8 py-4 text-right text-xs font-black text-gray-500 uppercase tracking-widest">اسم الكورس</th>
                                    <th className="px-8 py-4 text-right text-xs font-black text-gray-500 uppercase tracking-widest">السعر</th>
                                    <th className="px-8 py-4 text-right text-xs font-black text-gray-500 uppercase tracking-widest">المشتركين</th>
                                    <th className="px-8 py-4 text-right text-xs font-black text-gray-500 uppercase tracking-widest">إجمالي الإيرادات</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-50">
                                {earnings?.earnings?.data?.topPerformingCourses?.map((course, index) => (
                                    <tr key={index} className="hover:bg-primary-50/30 transition-colors group/row">
                                        <td className="px-8 py-6 whitespace-nowrap">
                                            <div className="flex items-center justify-center w-8 h-8 bg-primary-100 text-primary-600 rounded-full text-xs font-black">
                                                {index + 1}
                                            </div>
                                        </td>
                                        <td className="px-8 py-6 whitespace-nowrap">
                                            <div className="flex items-center gap-3">
                                                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                                                    <Briefcase size={16} className="text-gray-400" />
                                                </div>
                                                <div>
                                                    <p className="font-black text-sm">{course.title}</p>
                                                    <p className="text-[10px] text-gray-400">{selectedPeriod === 'quarter' ? `الربع ${course.quarter}` : selectedPeriod === 'week' ? `أسبوع ${course.week}` : course.month ? `${course.month}/${course.year}` : course.year}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6 whitespace-nowrap">
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-black bg-emerald-100 text-emerald-800">
                                                {course.price}ج.م
                                            </span>
                                        </td>
                                        <td className="px-8 py-6 whitespace-nowrap">
                                            <span className="font-black">{course.enrollments}</span>
                                        </td>
                                        <td className="px-8 py-6 whitespace-nowrap">
                                            <span className="font-black">{course.totalRevenue}ج.م</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>

        {/* Sidebar Info Section (1/3 Width) */}
        <div className="space-y-8">
            {/* Quick Tips Card */}
            <div className="bg-gray-900 rounded-[2.5rem] p-10 text-white relative overflow-hidden group shadow-2xl">
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary-600/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
                <div className="relative z-10 space-y-6">
                    <div className="bg-primary-600 w-14 h-14 rounded-2xl flex items-center justify-center shadow-xl shadow-primary-900/50">
                        <Sparkles size={28} />
                    </div>
                    <div className="space-y-2">
                        <h3 className="text-2xl font-black tracking-tight leading-tight">كيف تزيد <br /> من أرباحك؟</h3>
                        <p className="text-gray-400 text-sm font-medium leading-relaxed">
                            قم بتقديم كوبونات خصم دورية للطلاب الجدد، أو قم برفع محتوى مجاني جزئي لجذب المزيد من المشتركين لكورساتك المدفوعة.
                        </p>
                    </div>
                    <button className="w-full bg-white text-gray-900 font-black py-4 rounded-2xl hover:bg-primary-50 transition-all flex items-center justify-center gap-2 transform active:scale-95 shadow-xl">
                        تعرف على استراتيجيات النمو <ChevronLeft size={18} />
                    </button>
                </div>
            </div>

            {/* Payment Settings Short Card */}
            <div className="bg-white rounded-[2rem] p-8 border border-gray-100 shadow-sm space-y-6">
                <h3 className="text-lg font-black text-gray-900 flex items-center gap-3">
                    <Briefcase size={20} className="text-primary-600" /> بيانات الصرف
                </h3>
                <div className="p-5 rounded-2xl bg-gray-50 border border-gray-100 flex items-center gap-4">
                    <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm text-gray-400">
                        <CreditCard size={20} />
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">الحساب الأساسي</p>
                        <p className="text-sm font-black text-gray-800 leading-none mt-1">محفظة فودافون كاش</p>
                    </div>
                </div>
                <button className="w-full py-4 border border-gray-100 text-gray-500 font-bold text-xs rounded-2xl hover:bg-gray-50 transition-all uppercase tracking-widest">تحديث وسيلة الدفع</button>
            </div>
        </div>
      </div>
    </div>
  );
};


export default TeacherEarnings;